import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CheckCircle, XCircle, ArrowRight, RefreshCw, Clock, AlertCircle, Coins } from 'lucide-react';
import { PaymentVerifierTailwind } from '../components/PaymentVerifierTailwind';
import { supabase } from '../lib/supabase';
import { registerPurchaseTransaction } from '../lib/supabase';
import { PaykuService } from '../services/paykuService';
import PaymentErrorModal from '../components/PaymentErrorModal';
import { TransactionVerifier } from '../services/transactionVerifier';
import { PaykuValidator } from '../services/paykuValidator';
import { useVerification } from '../contexts/VerificationContext';

const PaymentReturn: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'pending'>('loading');
  const [message, setMessage] = useState<string>('Verificando el estado de tu pago...');
  const [countdown, setCountdown] = useState<number>(5);
  const [countdownInterval, setCountdownInterval] = useState<NodeJS.Timeout | null>(null);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [showDebugInfo, setShowDebugInfo] = useState<boolean>(false);
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [coinsAdded, setCoinsAdded] = useState<number>(0);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [transactionDetails, setTransactionDetails] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const checkStarted = useRef<boolean>(false);
  const pendingRedirectTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const { startVerification, endVerification, isVerifying } = useVerification();

  const navigate = useNavigate();
  const location = useLocation();
  
  // Estado para el modal de error de pago
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [paymentErrorDetails, setPaymentErrorDetails] = useState<{
    transactionId: string;
    errorType: string;
    message: string;
    details?: any;
  } | null>(null);
  
  // Esta función se utiliza para navegar de forma segura
  // evitando errores de actualización durante el renderizado
  const safeNavigate = useCallback((path: string) => {
    // Usar setTimeout para asegurar que la navegación ocurra después del renderizado
    setTimeout(() => {
      navigate(path);
    }, 0);
  }, [navigate]);

  // Iniciar cuenta regresiva para redirección
  const startRedirectCountdown = () => {
    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          safeNavigate('/coins');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    if (countdownInterval) {
        clearInterval(countdownInterval);
    }
    setCountdownInterval(interval);
  };

  // Verificación estricta (definida antes del useEffect principal)
  const verifyPaykuPaymentStrict = useCallback(async (idToVerify: string) => {
    if (!startVerification(idToVerify)) {
      console.log(`[PAYKU-RETURN] Verificación para ${idToVerify} ya en curso (iniciada por otro componente/instancia). Omitiendo.`);
      return;
    }

    try {
      console.log('[PAYKU-RETURN] Iniciando verificación estricta con PaykuValidator para ID:', idToVerify);
      
      // Usar el nuevo servicio de validación
      const result = await PaykuValidator.processPayment(idToVerify);
      
      console.log('[PAYKU-RETURN] Resultado de verificación estricta:', result);
      
      if (result.success) {
        // Pago verificado exitosamente
        setStatus('success');
        setMessage(result.message);
        setCoinsAdded(result.coinsAdded || 0);
        
        // Si hay detalles de la transacción, mostrarlos
        if (result.details) {
          setTransactionDetails({
            vcoinsAmount: result.coinsAdded || result.details.transaction?.vcoins_amount,
            packageName: result.details.transaction?.metadata?.package_name || 'Paquete de VCoins'
          });
        }
        
        // Emitir evento para actualizar el estado de la aplicación
        const event = new CustomEvent('payment-success', { 
          detail: { 
            transactionId,
            timestamp: Date.now() 
          } 
        });
        window.dispatchEvent(event);
        
        // Disparar evento para actualizar las monedas en el header
        window.dispatchEvent(new Event('coinsUpdated'));
        
        // Limpiar datos de transacción pendiente
        try {
          localStorage.removeItem('pendingTransaction');
          sessionStorage.removeItem('pendingTransaction');
          localStorage.removeItem('payku_transaction');
          sessionStorage.removeItem('payku_transaction');
        } catch (e) {
          console.error('[PAYKU-RETURN] Error al limpiar datos de transacción:', e);
        }
        
        startRedirectCountdown();
      } else {
        // El pago no fue exitoso
        console.error('[PAYKU-RETURN] Verificación de pago fallida:', result);
        
        // Si el pago fue cancelado, mostrar modal específico
        if (result.status === 'cancelled') {
          setPaymentErrorDetails({
            transactionId,
            errorType: 'PAYMENT_CANCELLED',
            message: 'El pago fue cancelado o rechazado en Payku.',
            details: result.details
          });
          
          setShowErrorModal(true);
        } else if (result.status === 'pending') {
          // Si está pendiente, mostrar mensaje específico
          setStatus('error');
          setMessage('El pago está pendiente de confirmación. Por favor, intenta verificar nuevamente en unos minutos.');
        } else {
          // Otro tipo de error
          setStatus('error');
          setMessage(result.message || 'No se pudo verificar el pago. Por favor, contacta a soporte.');
        }
      }
    } catch (error) {
      console.error('[PAYKU-RETURN] Error en verifyPaykuPaymentStrict:', error);
      setStatus('error');
      setMessage('Ocurrió un error al verificar el pago. Por favor, contacta a soporte.');
    } finally {
      endVerification(idToVerify);
    }
  }, [startVerification, endVerification, setStatus, setMessage, setCoinsAdded, setTransactionDetails, setShowErrorModal, setPaymentErrorDetails, startRedirectCountdown]);

  useEffect(() => {
    const checkPayment = async () => {
      // Comprobar y marcar al inicio de la ejecución
      if (checkStarted.current) {
        console.log('[PAYKU-RETURN] checkPayment ya iniciado, omitiendo ejecución duplicada.');
        return;
      }
      checkStarted.current = true;
      console.log('[PAYKU-RETURN] Marcando checkPayment como iniciado y procediendo...');

      try {
        setStatus('loading');
        
        // Obtener parámetros de la URL
        const params = new URLSearchParams(window.location.search);
        const urlId = params.get('id');
        const txid = params.get('txid');
        
        console.log('[PAYKU-RETURN] Parámetros URL:', { urlId, txid });
        
        // Obtener información de la transacción pendiente de sessionStorage y localStorage
        const pendingTransactionStr = sessionStorage.getItem('pendingTransaction') || localStorage.getItem('pendingTransaction');
        const pendingTransaction = pendingTransactionStr ? JSON.parse(pendingTransactionStr) : null;
        
        // También verificar en payku_transaction
        const paykuTransactionStr = sessionStorage.getItem('payku_transaction') || localStorage.getItem('payku_transaction');
        const paykuTransaction = paykuTransactionStr ? JSON.parse(paykuTransactionStr) : null;
        
        console.log('[PAYKU-RETURN] Transacción pendiente:', pendingTransaction);
        console.log('[PAYKU-RETURN] Transacción Payku:', paykuTransaction);
        
        // Determinar el ID de transacción a usar (priorizar URL)
        let transactionId = urlId || txid;
        
        // Si no hay ID en la URL, intentar obtenerlo del storage
        if (!transactionId) {
          if (pendingTransaction && pendingTransaction.id) {
            transactionId = pendingTransaction.id;
          } else if (paykuTransaction && paykuTransaction.id) {
            transactionId = paykuTransaction.id;
          }
        }
        
        if (!transactionId) {
          console.error('[PAYKU-RETURN] No se pudo determinar el ID de transacción');
          setStatus('error');
          setMessage('No se pudo identificar la transacción. Por favor, contacta a soporte.');
          return;
        }
        
        console.log('[PAYKU-RETURN] Verificando transacción con ID:', transactionId);
        setTransactionId(transactionId);
        
        // VERIFICACIÓN CRÍTICA: Verificar primero si la transacción fue cancelada
        const cancellationCheck = await TransactionVerifier.verifyTransactionWasCancelled(transactionId);
        
        if (cancellationCheck.cancelled) {
          console.error(`[PAYKU-RETURN] Transacción ${transactionId} está marcada como cancelada. Mostrando error.`);
          
          // Asegurar que esté marcada como cancelada en la base de datos
          await TransactionVerifier.markTransactionAsCancelled(
            transactionId, 
            `Detectado en página de retorno desde fuente: ${cancellationCheck.source}`
          );
          
          // Mostrar modal de error
          setPaymentErrorDetails({
            transactionId,
            errorType: 'PAYMENT_CANCELLED',
            message: 'El pago fue cancelado pero estás en la página de retorno de pago.'
          });
          
          setShowErrorModal(true);
          setStatus('error');
          return;
        }
        
        // Si no está cancelada, continuar con la verificación estricta
        await verifyPaykuPaymentStrict(transactionId);
      } catch (error) {
        console.error('[PAYKU-RETURN] Error al verificar el pago:', error);
        setStatus('error');
        setMessage('Ocurrió un error al verificar el pago. Por favor, intenta nuevamente.');
      }
    };
    
    checkPayment();
  }, [location.search, safeNavigate, verifyPaykuPaymentStrict]);

  // useEffect principal para la verificación inicial
  useEffect(() => {
    // ... (lógica de checkPayment con control checkStarted.current)
  }, [location.search, safeNavigate, verifyPaykuPaymentStrict]);

  // ---- NUEVO useEffect para redirección automática desde pendiente ----
  useEffect(() => {
    // Función para limpiar el timer
    const clearPendingTimer = () => {
      if (pendingRedirectTimerRef.current) {
        clearTimeout(pendingRedirectTimerRef.current);
        pendingRedirectTimerRef.current = null;
        console.log('[PAYKU-RETURN] Temporizador de redirección pendiente cancelado.');
      }
    };

    // Si el estado es error y el mensaje indica pendiente...
    if (status === 'error' && message.includes('pendiente de confirmación')) {
      console.log('[PAYKU-RETURN] Estado de error pendiente detectado. Iniciando temporizador de 10s para redirección a /coins.');
      clearPendingTimer(); // Limpiar timer anterior por si acaso
      pendingRedirectTimerRef.current = setTimeout(() => {
        // Volver a verificar el estado antes de redirigir
        // Necesitamos obtener el estado más actual aquí, useRef no es ideal para esto
        // Asumiremos que si el timer se completa, el estado no cambió favorablemente
        // Una mejor aproximación sería usar una función que obtenga el estado actual
        // Pero por simplicidad, si el timer corre, redirigimos.
        console.log('[PAYKU-RETURN] Temporizador de 10s completado. Redirigiendo a /coins.');
        safeNavigate('/coins');
      }, 10000); // 10 segundos
    } else {
      // Si el estado o mensaje cambian a algo diferente, limpiar el timer
      clearPendingTimer();
    }

    // Limpieza al desmontar o si las dependencias cambian
    return () => {
      clearPendingTimer();
    };
  }, [status, message, safeNavigate]); // Depender de status y message
  // ---- FIN NUEVO useEffect ----

  // Verificar manualmente
  const handleManualVerify = () => {
    if (!transactionId || loading) return;
    
    setLoading(true);
    setStatus('loading');
    setMessage('Verificando el estado de tu pago...');
    
    verifyPaykuPaymentStrict(transactionId)
      .finally(() => setLoading(false));
  };

  // Manejadores para el modal de error
  const handleRetryPayment = () => {
    // Cerrar el modal
    setShowErrorModal(false);
    
    // Usar safeNavigate en lugar de navigate directamente
    safeNavigate('/coins');
  };
  
  const handleGoBack = () => {
    // Cerrar el modal
    setShowErrorModal(false);
    
    // Usar safeNavigate en lugar de navigate directamente
    safeNavigate('/dashboard');
  };

  // Funciones de ayuda para depuración
  const toggleDebugInfo = () => {
    setShowDebugInfo(!showDebugInfo);
  };
  
  // Verificar manualmente - redirigir a la página de verificación completa
  const goToVerificationPage = () => {
    const id = transactionId || '';
    navigate(`/verify-payment?txid=${id}`);
  };
  
  // Renderizar directamente el componente de verificación si aún estamos en estado de carga
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full">
          <h1 className="text-2xl font-bold text-white mb-4 text-center">Verificación de Pago</h1>
          <PaymentVerifierTailwind 
            initialTransactionId={transactionId || undefined}
            onVerificationComplete={handleManualVerify}
            showForm={false}
            disableAutoVerify={true}
          />
          
          {/* Link oculto para mostrar información de depuración con 10 clics */}
          <div className="mt-8 text-center">
            <button 
              onClick={toggleDebugInfo} 
              className="text-gray-600 text-xs hover:text-gray-500"
            >
              .
            </button>
          </div>
          
          {showDebugInfo && (
            <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-700 text-xs text-gray-400 font-mono overflow-x-auto">
              <p className="font-semibold mb-1">Debug Info:</p>
              <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="mb-6">
            <Clock className="mx-auto h-16 w-16 text-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-4">Pago en Proceso</h1>
          <p className="text-gray-300 mb-6">{message}</p>
          
          <div className="p-4 bg-gray-900/50 rounded-lg mb-6">
            <p className="text-sm text-gray-300">
              Tu pago está siendo procesado por Payku. Este proceso puede tardar unos minutos.
            </p>
            <p className="text-sm text-gray-400 mt-2">
              ID de transacción: {transactionId}
            </p>
          </div>
          
          <div className="flex flex-col space-y-4">
            <button 
              onClick={goToVerificationPage}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition duration-300 flex items-center justify-center"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              <span>Verificar estado</span>
            </button>
            
            <Link 
              to="/dashboard" 
              className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded transition duration-300 flex items-center justify-center"
            >
              <span>Ir al Dashboard</span>
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="mb-4">
            <div className="bg-green-900/30 py-6 px-4 rounded-lg">
              <CheckCircle className="mx-auto h-20 w-20 text-green-500 mb-2" />
              <h1 className="text-3xl font-bold text-white mb-2">¡Pago Exitoso!</h1>
              <p className="text-xl text-gray-300">{message}</p>
            </div>
          </div>
          
          {/* Mostrar prominentemente las monedas añadidas */}
          <div className="bg-blue-900/30 p-5 rounded-lg mb-6 flex flex-col items-center">
            <Coins className="h-8 w-8 text-yellow-400 mb-2" />
            <div className="text-4xl font-bold text-yellow-400">+{coinsAdded}</div>
            <div className="text-gray-400">monedas añadidas a tu cuenta</div>
          </div>
          
          {/* Cuenta regresiva más visible */}
          <div className="mb-6">
            <div className="text-3xl font-bold text-white mb-1">{countdown}</div>
            <p className="text-sm text-gray-400">
              Serás redirigido a tu cuenta en {countdown} {countdown === 1 ? 'segundo' : 'segundos'}
            </p>
          </div>
          
          <div className="flex justify-center">
            <Link 
              to="/account" 
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition duration-300 flex items-center"
            >
              <span>Ir a mi cuenta ahora</span>
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
          
          {/* Link oculto para depuración */}
          <div className="mt-8">
            <button 
              onClick={toggleDebugInfo} 
              className="text-gray-600 text-xs hover:text-gray-500"
            >
              .
            </button>
          </div>
          
          {showDebugInfo && (
            <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-700 text-xs text-gray-400 font-mono overflow-x-auto">
              <p className="font-semibold mb-1">Debug Info:</p>
              <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="bg-gray-800 p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        {status === 'error' && (
          <>
            <div className="mb-6">
              <XCircle className="mx-auto h-16 w-16 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">Error en el Pago</h1>
            <p className="text-gray-300 mb-6">{message}</p>
            <div className="flex flex-col space-y-4">
              <PaymentVerifierTailwind
                initialTransactionId={transactionId || undefined}
                onVerificationComplete={handleManualVerify}
                showForm={true}
                disableAutoVerify={true}
              />
              
              <div className="flex flex-col space-y-4 mt-4">
                <Link 
                  to="/coins" 
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition duration-300 flex items-center justify-center"
                >
                  <span>Intentar Nuevamente</span>
                  <RefreshCw className="ml-2 h-4 w-4" />
                </Link>
                
                <Link 
                  to="/dashboard" 
                  className="bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded transition duration-300 flex items-center justify-center"
                >
                  <span>Volver al Dashboard</span>
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
            </div>
          </>
        )}
        
        {/* Link oculto para depuración */}
        <div className="mt-8">
          <button 
            onClick={toggleDebugInfo} 
            className="text-gray-600 text-xs hover:text-gray-500"
          >
            .
          </button>
        </div>
        
        {showDebugInfo && (
          <div className="mt-4 p-3 bg-gray-900 rounded border border-gray-700 text-xs text-gray-400 font-mono overflow-x-auto">
            <p className="font-semibold mb-1">Debug Info:</p>
            <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
          </div>
        )}
      </div>
      
      {/* Modal de error de pago */}
      <PaymentErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        transactionId={paymentErrorDetails?.transactionId}
        onRetry={handleRetryPayment}
        onGoBack={handleGoBack}
      />
    </div>
  );
};

export default PaymentReturn; 